import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { TooltipProps } from '@mui/material/Tooltip';
import { MarkerF } from '@react-google-maps/api';

import { MarkerTooltipOverlay } from './MarkerTooltipOverlay';

import { TOOLTIP_APPEAR_DELAY } from 'constants/common';

type Props = {
  id: string;
  position: {
    lat: number;
    lng: number;
  };
  title?: string;
  tooltip?: string | ReactElement | null;
  label?: google.maps.MarkerLabel | string;
  className?: string;
  tooltipProps?: Partial<TooltipProps>;
  zIndex?: number;
};

export const GoogleMarker = React.memo(
  ({
    id,
    position,
    title,
    tooltip,
    label,
    className,
    tooltipProps,
    zIndex,
  }: Props) => {
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

    const clearTooltipTimer = useCallback(() => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = null;
      }
      setShowTooltip(false);
    }, []);

    const startTooltipTimer = useCallback(() => {
      if (tooltip) {
        tooltipTimerRef.current = setTimeout(() => {
          setShowTooltip(true);
        }, TOOLTIP_APPEAR_DELAY);
      }
    }, [tooltip]);

    useEffect(() => {
      return () => {
        if (tooltipTimerRef.current) {
          clearTimeout(tooltipTimerRef.current);
        }
      };
    }, []);

    const marker = useMemo(
      () => (
        <MarkerF
          position={position}
          title={tooltip ? undefined : title}
          label={label}
          zIndex={zIndex}
          onMouseOver={startTooltipTimer}
          onMouseOut={clearTooltipTimer}
        />
      ),
      [
        position,
        title,
        label,
        zIndex,
        startTooltipTimer,
        clearTooltipTimer,
        tooltip,
      ],
    );

    if (!tooltip) {
      return marker;
    }

    return (
      <>
        {marker}
        <MarkerTooltipOverlay
          id={`${id}-tooltip`}
          position={position}
          tooltip={tooltip}
          open={showTooltip}
          className={className}
          tooltipProps={tooltipProps}
          zIndex={(zIndex || 10) + 1}
        />
      </>
    );
  },
);
